'use client';

import { cn } from '@/lib/utils';

import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import Badge from '@/decodo/components/atoms/badge';
import { IconButton } from '@/decodo/components/atoms/IconButton';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { Text } from '@/decodo/components/atoms/Text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/decodo/components/atoms/Toggle/useCMSToggle';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

import CardsWithHeader from '.';

import IconDownload from '~/icons/download-02.svg';

export interface CardsWithHeaderStrapiProps {
  header?: BlockHeader;
  cards: {
    icon: ImageStrapiProps;
    title: string;
    description: RichTextFieldStrapiProps;
    button: LinkButtonStrapiProps;
    downloadLink?: string;
    badgeText?: string;
  }[];
  toggle?: ToggleCMSProps;
}

export default function CardsWithHeaderStrapi({
  block,
  ...rest
}: BlockComponentModel<CardsWithHeaderStrapiProps>) {
  const { cards, header, toggle } = block;
  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof cards
  >(cards, toggle);

  function transformCard(card: (typeof cards)[number]) {
    return {
      icon: (
        <ImageStrapi
          inner={{ width: '24', height: '24' }}
          block={{ ...card.icon }}
        />
      ),
      title: (
        <Text
          color='base-black'
          variant='subtitle-l-d'
          classNames={{ wrapper: 'font-serif' }}
        >
          {card.title}
        </Text>
      ),
      description: (
        <RichTextFieldStrapi
          block={card.description}
          classNames={{ paragraph: 'text-body-l' }}
        />
      ),
      footerLeft: (
        <LinkButtonStrapi
          block={card.button}
          inner={{
            slim: true,
            variant: 'text',
            color: 'tertiary',
            showRightChevron: true,
          }}
        />
      ),
      footerRight: card.downloadLink ? (
        <IconButton
          variant='text'
          href={card.downloadLink}
          as='a'
          target='_blank'
          classNames={{ root: 'text-tertiary-500 p-0' }}
        >
          <IconDownload />
        </IconButton>
      ) : undefined,
      badge: card.badgeText ? (
        <Badge
          variant='filled'
          colors='gray-200'
          classNames={{ root: 'absolute top-3 right-3 z-10' }}
        >
          {card.badgeText}
        </Badge>
      ) : undefined,
    };
  }

  return (
    <BlockWrapper
      {...rest}
      data-tracker-section={SECTION_NAMES.CARDS_WITH_HEADER}
    >
      <div>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
        >
          <div
            className={cn(
              'flex w-full flex-wrap justify-center gap-6',
              'm:grid-cols-3 m:grid m:gap-4'
            )}
          >
            {cards.map((card) => (
              <div
                key={card.title}
                className={cn(
                  'w-full',
                  filteredToggleList?.length &&
                    !filteredToggleList?.some(
                      (item) => card.title === item.title
                    ) &&
                    'hide-content'
                )}
              >
                <CardsWithHeader {...transformCard(card)} />
              </div>
            ))}
          </div>
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
